import React, {useCallback} from 'react';

import TimelineIcon from '@mui/icons-material/Timeline';
import TheatersIcon from '@mui/icons-material/Theaters';

import {checkboxStateEnum, procedureTypeEnum, accessGroupEnum} from '../../../serverClientShared/sharedEnums';
import {ThreeDIcon} from '../../../assets/svg-icons/ThreeDIcon';
import {isMixedMemberAccessConflict} from './accessNodeHelpers';
import {AccessCheckbox, GROUP_CHECKED, GROUP_INDETERMINATE} from './AccessCheckbox';
import {AccessSourceLabel} from './AccessSourceLabel';
import {MixedDirectBadge} from './MixedDirectBadge';

const ICON_BY_PROCEDURE_TYPE = {
    [procedureTypeEnum.REGULAR]: TimelineIcon,
    [procedureTypeEnum.MEDIA]: TheatersIcon,
    [procedureTypeEnum.FUSION]: ThreeDIcon,
    [procedureTypeEnum.THREE_D]: ThreeDIcon,
    [procedureTypeEnum.DESIGNER]: TheatersIcon,
};

const useNodeRenderers = ({
    checkboxState, handleCheckToggle, getAccessInfo, isItemDisabled, isSharedEffective,
    isBulkMode, isFilterMode, mixed, edits, targets, groupMembership, getDisabledTooltip,
}) => {
    const renderNodePrefix = useCallback((node) => {
        if (isFilterMode) {
            return (
                <AccessCheckbox
                    data-testid={`access-checkbox-${node.name}`}
                    state={checkboxState(node.id)}
                    onToggle={() => handleCheckToggle(node.id)}
                />
            );
        }
        const disabled = isItemDisabled(node.id);
        const state = checkboxState(node.id);
        const accessInfo = !disabled ? getAccessInfo(node.id) : null;
        const publicEffective = isSharedEffective(node.id);
        const sharedInherited = accessInfo?.inheritedFrom?.filter(s => {
            if (s.sourceId === accessGroupEnum.WORKSPACE_PUBLIC) return publicEffective;
            if (!isBulkMode) return true;
            if (s.sourceId === accessGroupEnum.GUEST) return true;
            return groupMembership.get(s.sourceId)?.size === targets.length;
        });
        const groupState = sharedInherited?.length
            ? (accessInfo.inheritedIncludeAll ? GROUP_CHECKED : GROUP_INDETERMINATE)
            : null;
        const hasDirectState = state !== checkboxStateEnum.EMPTY;
        return (
            <div className="flex items-center">
                {disabled ? (
                    <AccessCheckbox data-testid={`access-checkbox-${node.name}`} state="empty" disabled />
                ) : hasDirectState ? (
                    <AccessCheckbox
                        data-testid={`access-checkbox-${node.name}`}
                        state={state}
                        onToggle={() => handleCheckToggle(node.id)}
                    />
                ) : groupState ? (
                    <AccessCheckbox data-testid={`access-checkbox-${node.name}`} state={groupState} onToggle={() => handleCheckToggle(node.id)} />
                ) : (
                    <AccessCheckbox
                        data-testid={`access-checkbox-${node.name}`}
                        state={state}
                        onToggle={() => handleCheckToggle(node.id)}
                    />
                )}
                {isBulkMode && isMixedMemberAccessConflict(node.id, mixed, edits) && (
                    <MixedDirectBadge mixedInfo={mixed.get(node.id)} targets={targets} />
                )}
            </div>
        );
    }, [checkboxState, handleCheckToggle, isItemDisabled, isSharedEffective, isBulkMode, isFilterMode, mixed, edits, targets]);

    const renderNodeSuffix = useCallback((node, nodeType) => {
        if (isFilterMode) return null;
        const state = checkboxState(node.id);
        const hasDirectAccess = state !== checkboxStateEnum.EMPTY;
        return (
            <AccessSourceLabel
                accessInfo={getAccessInfo(node.id)}
                hasDirectAccess={hasDirectAccess}
                isBulkMode={isBulkMode}
                groupMembership={groupMembership}
                targetCount={targets.length}
                targets={targets}
                nodeType={nodeType}
                isPublicEffective={isSharedEffective(node.id)}
            />
        );
    }, [getAccessInfo, checkboxState, isSharedEffective, isBulkMode, isFilterMode, groupMembership, targets]);

    const getNodeClassName = useCallback((node) => {
        if (!isFilterMode && isItemDisabled(node.id)) return 'text-main-disabled cursor-not-allowed';
        return 'text-main-normal';
    }, [isItemDisabled, isFilterMode]);

    const getNodeTooltip = useCallback((node) => {
        if (isFilterMode) return undefined;
        if (!isItemDisabled(node.id)) return undefined;
        return getDisabledTooltip(node.id);
    }, [isItemDisabled, isFilterMode, getDisabledTooltip]);

    const getNodeIcon = useCallback((node) => {
        if (node.procedureType == null) return undefined;
        return ICON_BY_PROCEDURE_TYPE[node.procedureType] || undefined;
    }, []);

    return {renderNodePrefix, renderNodeSuffix, getNodeClassName, getNodeTooltip, getNodeIcon};
};

export default useNodeRenderers;
